import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import {
  Users, UserPlus, Crown, Shield, User, Eye, Trash2,
  Home, KeyRound, Copy, PackageCheck, ArrowRight, FileText, Boxes,
} from "lucide-react";

const ROLE_META = {
  owner: { label: "Owner", icon: Crown, color: "#F59E0B" },
  admin: { label: "Admin", icon: Shield, color: "#E05A36" },
  member: { label: "Member", icon: User, color: "#06B6D4" },
  guest: { label: "Guest", icon: Eye, color: "#6B7280" },
};

export default function Foyer() {
  const navigate = useNavigate();
  const [hh, setHh] = useState(null);
  const [invite, setInvite] = useState({ email: "", name: "", role: "member" });

  // housing transfer
  const [ht, setHt] = useState({ to_email: "", include_docs: true, include_history: true });
  const [htResult, setHtResult] = useState(null);
  const [htList, setHtList] = useState([]);
  const [htOpen, setHtOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [claimCode, setClaimCode] = useState("");
  const [claimPreview, setClaimPreview] = useState(null);

  const load = () => {
    api.get("/household").then((r) => setHh(r.data));
    api.get("/housing/transfers").then((r) => setHtList(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const doInvite = async () => {
    if (!invite.email.trim()) return toast.error("Email requis");
    await api.post("/household/invite", invite);
    toast.success("Invitation envoyée");
    setInvite({ email: "", name: "", role: "member" });
    load();
  };
  const remove = async (email) => { await api.delete(`/household/members/${encodeURIComponent(email)}`); toast.success("Membre retiré"); load(); };

  const createHousing = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/housing/transfer", ht);
      setHtResult(data);
      toast.success(`Carnet prêt à céder — ${data.item_count} bien(s)`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const copyCode = (code) => { navigator.clipboard?.writeText(code); toast.success("Code copié"); };

  const previewClaim = async () => {
    const code = claimCode.trim().toUpperCase();
    if (code.length < 4) return toast.error("Code invalide");
    try {
      const { data } = await api.get(`/transfers/${code}`);
      setClaimPreview(data);
    } catch (e) {
      setClaimPreview(null);
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const doClaim = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/transfers/${claimCode.trim().toUpperCase()}/claim`);
      toast.success(`${data.items_imported} bien(s) importé(s) dans ta mémoire`);
      setClaimPreview(null); setClaimCode("");
      navigate("/app/memoire");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!hh) return <div className="text-muted-foreground">Chargement…</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">{hh.name}</h1>
        <p className="text-muted-foreground text-sm mt-1">Partage familial : invite ton conjoint et tes enfants. Chaque bien peut être privé, partagé au foyer ou rattaché au logement.</p>
      </div>

      <div className="omy-glass rounded-2xl p-6 space-y-4">
        <h2 className="font-display font-medium flex items-center gap-2"><UserPlus size={18} className="text-primary" /> Inviter un membre</h2>
        <div className="grid sm:grid-cols-3 gap-3">
          <div><Label>Email</Label><Input data-testid="invite-email-input" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} className="mt-1 bg-black/30" placeholder="conjoint@email.com" /></div>
          <div><Label>Prénom</Label><Input data-testid="invite-name-input" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} className="mt-1 bg-black/30" placeholder="Camille" /></div>
          <div><Label>Rôle</Label>
            <Select value={invite.role} onValueChange={(v) => setInvite({ ...invite, role: v })}>
              <SelectTrigger data-testid="invite-role-select" className="mt-1 bg-black/30"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="member">Member</SelectItem>
                <SelectItem value="guest">Guest</SelectItem>
              </SelectContent>
            </Select></div>
        </div>
        <Button data-testid="invite-submit-btn" onClick={doInvite} className="bg-primary hover:bg-primary/90 rounded-full">Inviter</Button>
      </div>

      {/* Transfert du logement */}
      <div className="omy-glass rounded-2xl p-6 space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: "hsl(13 74% 54% / 0.14)" }}>
            <Home size={20} className="text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display font-medium">Transfert du logement</h2>
            <p className="text-sm text-muted-foreground mt-1">Vous vendez votre maison ? Cédez le carnet complet — plomberie, chauffage, équipements, garanties et documents — au nouveau propriétaire, sans partager vos données privées.</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          {/* Céder */}
          <div className="rounded-2xl border border-white/10 p-4 space-y-3">
            <h3 className="text-sm font-medium flex items-center gap-2"><PackageCheck size={16} className="text-primary" /> Céder mon carnet</h3>
            <Dialog open={htOpen} onOpenChange={(o) => { setHtOpen(o); if (o) setHtResult(null); }}>
              <DialogTrigger asChild>
                <Button data-testid="housing-transfer-open" className="bg-primary hover:bg-primary/90 rounded-full w-full">Préparer le transfert</Button>
              </DialogTrigger>
              <DialogContent data-testid="housing-transfer-dialog" className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2"><Home size={18} className="text-primary" /> Céder le carnet du logement</DialogTitle>
                  <DialogDescription>Un code sécurisé sera généré. Le nouveau propriétaire l'utilise pour récupérer tout l'historique du logement.</DialogDescription>
                </DialogHeader>
                {!htResult ? (
                  <div className="space-y-4 pt-1">
                    <div>
                      <Label>Email du nouveau propriétaire (optionnel)</Label>
                      <Input data-testid="housing-to-email" value={ht.to_email} onChange={(e) => setHt({ ...ht, to_email: e.target.value })} className="mt-1 bg-black/30" placeholder="acheteur@email.com" />
                    </div>
                    <label className="flex items-center justify-between gap-3 text-sm">
                      <span className="flex items-center gap-2"><FileText size={15} className="text-muted-foreground" /> Inclure les documents</span>
                      <Switch data-testid="housing-include-docs" checked={ht.include_docs} onCheckedChange={(v) => setHt({ ...ht, include_docs: v })} />
                    </label>
                    <label className="flex items-center justify-between gap-3 text-sm">
                      <span className="flex items-center gap-2"><Boxes size={15} className="text-muted-foreground" /> Inclure l'historique (entretiens, événements)</span>
                      <Switch data-testid="housing-include-history" checked={ht.include_history} onCheckedChange={(v) => setHt({ ...ht, include_history: v })} />
                    </label>
                    <Button data-testid="housing-transfer-create" onClick={createHousing} disabled={busy} className="bg-primary hover:bg-primary/90 rounded-full w-full">
                      {busy ? "Préparation…" : "Générer le code de transfert"}
                    </Button>
                  </div>
                ) : (
                  <div data-testid="housing-transfer-result" className="space-y-3 pt-1 text-center">
                    <p className="text-sm text-muted-foreground">{htResult.item_count} bien(s) · {htResult.doc_count} document(s) prêts à transférer.</p>
                    <div className="rounded-2xl bg-black/40 border border-primary/30 p-4">
                      <p className="text-xs text-muted-foreground mb-1">Code de transfert</p>
                      <p data-testid="housing-transfer-code" className="font-mono text-3xl font-bold tracking-widest text-primary">{htResult.code}</p>
                    </div>
                    <Button data-testid="housing-copy-code" onClick={() => copyCode(htResult.code)} variant="outline" className="rounded-full w-full">
                      <Copy size={15} className="mr-2" /> Copier le code
                    </Button>
                    <p className="text-xs text-muted-foreground">Communique ce code au nouveau propriétaire. Il pourra réclamer le carnet depuis son compte OMY.</p>
                  </div>
                )}
              </DialogContent>
            </Dialog>
            {htList.length > 0 && (
              <div className="space-y-1.5 pt-1">
                {htList.map((t) => (
                  <div key={t.id} data-testid={`housing-transfer-item-${t.code}`} className="flex items-center gap-2 text-xs rounded-lg bg-black/20 px-3 py-2">
                    <span className="font-mono font-medium tracking-wider text-primary">{t.code}</span>
                    <span className="text-muted-foreground">{t.item_count} bien(s)</span>
                    <span className={`ml-auto px-2 py-0.5 rounded-full ${t.status === "claimed" ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}>
                      {t.status === "claimed" ? "Réclamé" : "En attente"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Réclamer */}
          <div className="rounded-2xl border border-white/10 p-4 space-y-3">
            <h3 className="text-sm font-medium flex items-center gap-2"><KeyRound size={16} className="text-primary" /> Réclamer un carnet reçu</h3>
            <div className="flex gap-2">
              <Input data-testid="claim-code-input" value={claimCode} onChange={(e) => { setClaimCode(e.target.value); setClaimPreview(null); }} className="bg-black/30 font-mono tracking-widest uppercase" placeholder="CODE" />
              <Button data-testid="claim-preview-btn" onClick={previewClaim} variant="outline" className="rounded-full shrink-0">Aperçu</Button>
            </div>
            {claimPreview && (
              <div data-testid="claim-preview" className="rounded-2xl bg-black/20 p-3 space-y-2">
                <p className="text-sm">De <span className="font-medium">{claimPreview.from_name || "un particulier"}</span> · {claimPreview.item_count} bien(s) · {claimPreview.doc_count} doc(s)</p>
                {claimPreview.status === "claimed" ? (
                  <p className="text-xs text-amber-400">Ce transfert a déjà été réclamé.</p>
                ) : (
                  <>
                    <div className="flex flex-wrap gap-1.5">
                      {(claimPreview.items || []).slice(0, 8).map((i, k) => (
                        <span key={k} className="text-xs bg-black/30 rounded-full px-2 py-1">{i.name}</span>
                      ))}
                    </div>
                    <Button data-testid="claim-confirm-btn" onClick={doClaim} disabled={busy} className="bg-primary hover:bg-primary/90 rounded-full w-full">
                      {busy ? "Import…" : <>Réclamer ce carnet <ArrowRight size={15} className="ml-1.5" /></>}
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <h2 className="font-mono text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2"><Users size={14} /> Membres du foyer</h2>
        {hh.members?.map((m, i) => {
          const meta = ROLE_META[m.role] || ROLE_META.member;
          const Icon = meta.icon;
          return (
            <div key={m.email || i} data-testid={`member-${i}`} className="omy-glass rounded-2xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: `${meta.color}22` }}>
                <Icon size={18} style={{ color: meta.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{m.name} {m.status === "invited" && <span className="text-xs text-muted-foreground">(invité·e)</span>}</p>
                <p className="text-xs text-muted-foreground truncate">{m.email}</p>
              </div>
              <span className="text-xs font-medium px-2.5 py-1 rounded-full shrink-0" style={{ color: meta.color, background: `${meta.color}18` }}>{meta.label}</span>
              {m.role !== "owner" && (
                <button data-testid={`member-remove-${i}`} onClick={() => remove(m.email)} className="text-muted-foreground hover:text-destructive shrink-0"><Trash2 size={16} /></button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
